import { HiOutlineSwitchVertical } from '@/components/react-icons/main';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors.js';
import routes from '@/routes.js';
import isProjectCompleted from '@/lib/isProjectCompleted';
import { isApiIntegration } from '@/lib/isApiIntegration.js';
import getEmbeddedIntegrationRoute from '@/lib/getEmbeddedIntegrationRoute.js';
import { useSelector } from 'react-redux';

const addSlashes = (arr) => (Array.isArray(arr) ? arr : []).flatMap((item) => [{ value: '/', type: 'divider' }, item]);

const getBaseItems = ({ exclude, canViewFullService }) => {
  const items = [
    { label: 'Home', value: 'home', route: '/' }, //
    ...(canViewFullService ? [{ label: 'Full Service', value: 'full-service', route: '/full-service' }] : []),
    { label: 'Recipes', value: 'recipes', route: '/recipes' },
    { label: 'Workflows', value: 'workflows', route: '/workflows' },
    { label: 'Connections', value: 'credentials', route: '/credentials' },
    { label: 'Apps', value: 'apps', route: '/apps' },
    { label: 'Embedded', value: 'embedded', route: '/embedded' },
    { label: 'Jobs', value: 'jobs', route: '/jobs' },
    { label: 'Monitoring', value: 'monitoring', route: '/monitoring' },
    { label: 'Settings', value: 'settings', route: '/user/organization' }
  ];

  return items.filter((el) => el.value !== exclude);
};

const usePlatformBreadcrumb = () => {
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const activeRoute = routes.find((n) => n.id === activeRouteId);
  const isAdmin = useSelector(selectors.admin);
  const projects = useSelector((state) => selectors.companyIntegrationsProjects(state));
  const hasIncompleteProject = (projects || []).some((project) => !isProjectCompleted(project));
  const canViewFullService = isAdmin && hasIncompleteProject;

  const jobId = useArgSelector(selectors.param, 'jobId');
  const jobOptions = useSelector(
    memoizeSelector('breadcrumbJobOptions', (state) =>
      selectors
        .jobs(state)
        ?.filter((n) => n?.id !== jobId)
        ?.map((el) => ({ label: el.name, value: el.id, route: `/jobs/${el.id}` }))
    )
  );
  const activeJobName = useSelector((state) => selectors.jobs(state)?.find((n) => n?.id === jobId)?.name);

  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const workflowOptions = useSelector(
    memoizeSelector('breadcrumbWorkflowOptions', (state) =>
      selectors
        .workflowsMetadata(state)
        ?.filter((n) => n?.isDeleted !== true && n?.id !== workflowId)
        ?.map((el) => ({ label: el.name, value: el.id, route: `/workflows/${el.id}/${el.version}` }))
    )
  );
  const activeWorkflowName = useSelector((state) => selectors.workflowsMetadata(state)?.find((n) => n?.id === workflowId)?.name);

  const appId = useArgSelector(selectors.param, 'appId');
  const appOptions = useSelector(
    memoizeSelector(`appOptions-${appId}`, (state) =>
      selectors
        .appsMetadata(state)
        ?.filter((n) => n?.id !== appId)
        ?.map((el) => ({ label: el.name, value: el.id, route: `/apps/${el.id}` }))
    )
  );
  const activeAppName = useSelector((state) => selectors.appsMetadata(state)?.find((n) => n?.id === appId)?.name);

  const integrationId = useArgSelector(selectors.param, 'integrationId');
  const integrationOptions = useSelector(
    memoizeSelector('breadcrumbIntegrationOptions', (state) =>
      selectors
        .embeddedIntegrations(state)
        ?.filter((n) => n?.isDeleted !== true && n?.id !== integrationId)
        ?.map((el) => ({ label: el.name, value: el.id, route: getEmbeddedIntegrationRoute(el) }))
    )
  );
  const activeIntegration = useSelector((state) => selectors.embeddedIntegrations(state)?.find((n) => n?.id === integrationId));
  const activeIntegrationName = activeIntegration?.name;

  if (activeRoute?.path === '/') {
    return [];
  }

  const stack = [];

  if (activeRoute?.routeId === 'jobs' && jobId && activeJobName) {
    stack.push({ value: 'Jobs', route: '/jobs', items: getBaseItems({ exclude: 'jobs', canViewFullService }), dataTitle: 'Go back to job list' });
    stack.push({
      value: activeJobName,
      items: jobOptions,
      dataTitle: 'Switch jobs',
      icon: HiOutlineSwitchVertical
    });

    return addSlashes(stack);
  }

  if (activeRoute?.routeId === 'workflows' && workflowId && activeWorkflowName) {
    stack.push({ value: 'Workflows', route: '/workflows', items: getBaseItems({ exclude: 'workflows', canViewFullService }), dataTitle: 'Go back to workflow list', icon: HiOutlineSwitchVertical });
    stack.push({
      value: activeWorkflowName,
      items: workflowOptions,
      dataTitle: 'Switch workflows',
      icon: HiOutlineSwitchVertical
    });

    return addSlashes(stack);
  }

  if (activeRoute?.routeId === 'apps' && appId && activeAppName) {
    stack.push({ value: 'Apps', route: '/apps', items: getBaseItems({ exclude: 'apps', canViewFullService }), dataTitle: 'Go back to app list' });
    stack.push({
      value: activeAppName,
      items: appOptions,
      dataTitle: 'Switch apps',
      icon: HiOutlineSwitchVertical
    });

    return addSlashes(stack);
  }

  if (activeRoute?.routeId === 'embedded' && integrationId && activeIntegrationName) {
    stack.push({ value: 'Integrations', route: isApiIntegration(activeIntegration) ? '/embedded/integrations?type=api' : '/embedded/sdk', items: getBaseItems({ exclude: 'embedded', canViewFullService }), dataTitle: 'Go back to integrations list', icon: HiOutlineSwitchVertical });
    stack.push({
      value: activeIntegrationName,
      items: integrationOptions,
      dataTitle: 'Switch integrations',
      icon: HiOutlineSwitchVertical
    });

    return addSlashes(stack);
  }

  if (activeRoute?.routeId === 'adminSettings') {
    stack.push({ value: 'Admin Settings', route: '/admin-settings/companies', items: getBaseItems({ exclude: 'settings', canViewFullService }), dataTitle: 'Go back to admin settings' });

    return addSlashes(stack);
  }

  return addSlashes(stack);
};

export default usePlatformBreadcrumb;
